import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessagesSquareIcon, NewspaperIcon } from 'lucide-react';
import { DomainHeader } from '../../components/ui/DomainHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { ChoiceGroup } from '../../components/ui/ChoiceGroup';
import { EmptyState } from '../../components/ui/EmptyState';
import { CampusToday } from '../../components/social/CampusToday';
import { PostComposer } from '../../components/social/PostComposer';
import { PostCard } from '../../components/social/PostCard';
import { CommunitiesPanel } from '../../components/social/CommunitiesPanel';
import { ClubsPanel } from '../../components/social/ClubsPanel';
import { ChallengesPanel } from '../../components/social/ChallengesPanel';
import { useSocial } from '../../contexts/SocialContext';
import { orderFeed } from '../../utils/social';
const TABS = ['Today', 'Feed', 'Questions', 'Communities', 'Clubs', 'Challenges'] as const;
const SCOPES = ['My campus', 'My communities', 'All Campuzon'] as const;

/**
 * Social & Campus Life. This is the human layer over the rest of Campuzon —
 * every card here either belongs to another domain's record or links into it.
 */
export function Social() {
  const {
    posts,
    hidden,
    communities,
    me
  } = useSocial();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Today');
  const [scope, setScope] = useState<(typeof SCOPES)[number]>('My campus');
  const joinedIds = useMemo(() => communities.filter(community => community.joined).map(community => community.id), [communities]);
  const feed = useMemo(() => {
    const visible = posts.filter(post => !hidden.includes(post.id) && post.kind !== 'question');
    const scoped = visible.filter(post => {
      if (scope === 'All Campuzon') return true;
      if (scope === 'My communities') return post.spaceId !== null && joinedIds.includes(post.spaceId);
      return post.campusId === 'cmp-futa-main';
    });
    return orderFeed(scoped);
  }, [hidden, joinedIds, posts, scope]);
  const questions = useMemo(() => orderFeed(posts.filter(post => post.kind === 'question' && !hidden.includes(post.id))), [hidden, posts]);
  return <div className="space-y-6" data-id="element-10843" data-mp="3nbl1hskof8g9">
      <DomainHeader domain="social" eyebrow="Campus life" title="Social" description="What is happening around you, who you know, and which communities you belong to — connected to the rest of Campuzon rather than sitting beside it." actions={<div className="flex flex-wrap items-center gap-2" data-id="element-10845" data-mp="61woeu3ahev6">
            <Badge plain data-id="element-10846" data-mp="a388yl7b39wi">@{me.handle}</Badge>
            <Link to="/community" className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line px-4 text-body-sm font-medium text-ink hover:border-brand-300" data-id="element-10847" data-mp="a26t83fchz8g">
              <MessagesSquareIcon aria-hidden className="h-4 w-4" data-id="element-10848" data-mp="2napyu02esvuj" />
              People, groups and messages
            </Link>
          </div>} data-id="element-10844" data-mp="3ncme41hcc303" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Social sections" data-id="element-10849" data-mp="2ib3llqmkzflq" />

      {tab === 'Today' && <CampusToday data-id="element-10850" data-mp="2wzzutr0diynx" />}

      {tab === 'Feed' && <div className="space-y-5" data-id="element-10851" data-mp="2s1pt0xk58ztc">
          <Panel title="What you are seeing" data-id="element-10852" data-mp="2s2r77awgi2fa">
            <ChoiceGroup label="Scope" options={SCOPES} value={scope} onChange={setScope} data-id="element-10853" data-mp="24bvm0wmia5h0" />
            <p className="mt-3 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-10854" data-mp="24doqc59ncdlu">
              Your campus comes first. Broader Campuzon content is available, but never dominates
              your feed by default.
            </p>
          </Panel>
          <PostComposer data-id="element-10855" data-mp="299x35c0dfupu" />
          {feed.length === 0 ? <Panel data-id="element-10856" data-mp="3pp6qmi8g1a5h">
              <EmptyState icon={<NewspaperIcon className="h-5 w-5" data-id="element-10858" data-mp="3pnvpkq6dugi2" />} title="Nothing in this scope" description="Widen the scope, or join a community to see what is being discussed there." data-id="element-10857" data-mp="3poovjoxip1z5" />
            </Panel> : <ul className="space-y-4" data-id="element-10859" data-mp="1z7tbsmjvd2ee">
              {feed.map(post => <li key={post.id} data-id="element-10860" data-mp="1z9xr00n7xntz">
                  <PostCard post={post} data-id="element-10861" data-mp="239ey0yiamt19" />
                </li>)}
            </ul>}
        </div>}

      {tab === 'Questions' && <div className="space-y-5" data-id="element-10862" data-mp="286esec5s67cj">
          <Panel title="Campus questions" data-id="element-10863" data-mp="2d8du7hlvgw7d">
            <p className="max-w-[76ch] text-body-sm text-ink" data-id="element-10864" data-mp="2d7m1qfnknelb">
              Unresolved questions come first. An answer from a verified provider, an organisation or
              your institution is marked as verified information; everything else is labelled as
              personal opinion, including answers written by students and by AI.
            </p>
          </Panel>
          <PostComposer data-id="element-10865" data-mp="2vzokpxnor6yq" />
          <ul className="space-y-4" data-id="element-10866" data-mp="2w06n7c0xyhf3">
            {questions.map(post => <li key={post.id} data-id="element-10867" data-mp="30y85huqjmcpi">
                <PostCard post={post} data-id="element-10868" data-mp="30v3w8iy80qrz" />
              </li>)}
          </ul>
        </div>}

      {tab === 'Communities' && <CommunitiesPanel data-id="element-10869" data-mp="361r6akeb7fw1" />}
      {tab === 'Clubs' && <ClubsPanel data-id="element-10870" data-mp="360zb3aomjk6a" />}
      {tab === 'Challenges' && <ChallengesPanel data-id="element-10871" data-mp="312nzauerqzlf" />}
    </div>;
}